import { JobStatus } from 'jobs/dtos/job'
import { DateRange } from 'infra/FilterControls/DateRange/DateRangeFilter'
import { NumberRange } from 'infra/FilterControls/NumberRange/NumberRangeFilter'
import { Id } from 'types/definitions'
import { JobProgressionStepStatus } from 'jobs/progression/JobProgression'
import { JobScopeChangeRequestStatus } from 'jobs/dtos/job-scope-change'
import { JobPeriodFilterOption } from 'jobs/views/JobManagement/JobPeriodFilterOption'

/**
 * Query string builder for the Job Search API.
 */
export default class JobSearchQueryStringBuilder {
  legends = ''
  workspaces = ''
  statuses = ''
  dateCreated = ''
  dateClosed = ''
  dateClaimed = ''
  dateSubmitted = ''
  providerCutAmount = ''
  progressionSteps = ''
  jobScopeChangeStatus = ''
  teams = ''
  period = ''
  archived = ''
  readyToAdvance = ''
  members = ''
  serviceItems = ''
  documentTypes = ''

  /**
   * Searches for jobs with any legend id.
   *
   * @param ids
   * @returns
   */
  withLegendIds(ids?: Id[] | null): JobSearchQueryStringBuilder {
    if (!ids) {
      return this
    }

    this.legends += ids
      .filter(Boolean)
      .map((id) => condition('legend_id', '=', id))
      .join(' OR ')
    return this
  }

  /**
   * Searches for jobs within workspaces.
   *
   * @param ids
   */
  withWorkspaceIds(ids?: Id[] | null): JobSearchQueryStringBuilder {
    if (!ids) {
      return this
    }

    this.workspaces += ids
      .filter(Boolean)
      .map((id) => condition('workspace_id', '=', id))
      .join(' OR ')
    return this
  }

  /**
   * Searches for jobs with any status.
   *
   * @param statuses
   * @returns
   */
  withStatuses(statuses?: JobStatus[] | null): JobSearchQueryStringBuilder {
    if (!statuses) {
      return this
    }

    this.statuses += statuses
      .filter(Boolean)
      .map((s) => condition('status', '=', s))
      .join(' OR ')
    return this
  }

  /**
   * Searches for jobs with any progression step
   */
  withProgressionSteps(
    progressionSteps?: JobProgressionStepStatus[] | null,
  ): JobSearchQueryStringBuilder {
    if (!progressionSteps) {
      return this
    }

    this.progressionSteps += progressionSteps
      .map((s) =>
        condition('progression_step', '=', JobProgressionStepStatus[s]),
      )
      .join(' OR ')

    return this
  }

  /**
   * Searches for jobs with any job scope change request status.
   */
  withJobScopeChangeStatus(
    jobScopeChangeStatus?: JobScopeChangeRequestStatus[] | null,
  ): JobSearchQueryStringBuilder {
    if (!jobScopeChangeStatus) {
      return this
    }

    this.jobScopeChangeStatus += jobScopeChangeStatus
      .map((s) =>
        condition('change_request_status', '=', JobScopeChangeRequestStatus[s]),
      )
      .join(' OR ')

    return this
  }

  /**
   * Searches for jobs where date created is within date range.
   *
   * @param dateRange
   * @returns
   */
  withinDateCreatedRange(
    dateRange?: DateRange | null,
  ): JobSearchQueryStringBuilder {
    if (!dateRange) {
      return this
    }

    this.dateCreated += this.rangeQueryParts(
      FieldString.DateCreated,
      dateRange,
    ).join(' AND ')
    return this
  }

  /**
   * Searches for jobs where date closed is within date range.
   *
   * @param dateRange
   * @returns
   */
  withinDateClosedRange(
    dateRange?: DateRange | null,
  ): JobSearchQueryStringBuilder {
    if (!dateRange) {
      return this
    }

    this.dateClosed += this.rangeQueryParts(
      FieldString.DateCompleted,
      dateRange,
    ).join(' AND ')
    return this
  }

  /**
   * Searched for jobs where date claimed is within date range.
   *
   * @param dateRange
   * @returns
   */
  withinDateClaimedRange(
    dateRange?: DateRange | null,
  ): JobSearchQueryStringBuilder {
    if (!dateRange) {
      return this
    }

    this.dateClaimed += this.rangeQueryParts(
      FieldString.DateClaimed,
      dateRange,
    ).join(' AND ')
    return this
  }

  /**
   * Searches for jobs where date submitted is within date range.
   *
   * @param dateRange
   * @returns
   */
  withinDateSubmittedRange(
    dateRange?: DateRange | null,
  ): JobSearchQueryStringBuilder {
    if (!dateRange) {
      return this
    }

    this.dateSubmitted += this.rangeQueryParts(
      FieldString.DateSubmitted,
      dateRange,
    ).join(' AND ')
    return this
  }

  withProviderCutAmountRange(
    providerCutAmountRange?: NumberRange | null,
  ): JobSearchQueryStringBuilder {
    if (!providerCutAmountRange) {
      return this
    }

    this.providerCutAmount += this.rangeQueryParts(
      FieldString.ProviderCutAmount,
      providerCutAmountRange,
    ).join(' AND ')
    return this
  }

  withTeamIds(ids?: Id[] | null): JobSearchQueryStringBuilder {
    if (!ids) {
      return this
    }

    this.teams += ids
      .filter(Boolean)
      .map((id) => {
        const client_team = condition('client_team_id', '=', id)
        const pro_team = condition('pro_team_id', '=', id)

        return `${client_team} OR ${pro_team}`
      })
      .join(' OR ')

    return this
  }

  withMemberIds(ids?: Id[] | null): JobSearchQueryStringBuilder {
    if (!ids) {
      return this
    }

    this.members += ids
      .filter(Boolean)
      .map((id) => condition('members.user_public_id', ':', id))
      .join(' OR ')

    return this
  }

  withServiceItems(ids?: Id[] | null): JobSearchQueryStringBuilder {
    if (!ids) {
      return this
    }

    this.serviceItems = ids
      .filter(Boolean)
      .map((id) => condition('job_scope.inventory_item_ids', ':', id))
      .join(' OR ')

    return this
  }

  withDocumentTypeShortIds(ids?: Id[] | null): JobSearchQueryStringBuilder {
    if (!ids) {
      return this
    }

    this.documentTypes += ids
      .filter(Boolean)
      .map((id) => condition('document_type_short_ids', ':', id))
      .join(' OR ')

    return this
  }

  withPeriod(period: JobPeriodFilterOption): JobSearchQueryStringBuilder {
    const currentYear = new Date().getFullYear()
    const startOfTheYear = new Date(currentYear, 0, 1).toISOString()
    const endOfTheYear = new Date(currentYear, 11, 31, 23, 59, 59).toISOString()

    const claimed = '(status=CLAIMED OR status=IDLE OR status=ON_HOLD)'
    const submittedCurrentYear = `(date_submitted >= "${startOfTheYear}" AND date_submitted <= "${endOfTheYear}")`
    const createdCurrentYear = `((status=UNDER_CONSTRUCTION OR status=INFO_GATHERING OR status=UNCLAIMED OR status=PENDING) AND (date_created >= "${startOfTheYear}" AND date_created <= "${endOfTheYear}"))`

    const closedPreviousYears = `(status=CLOSED AND date_submitted < "${startOfTheYear}")`
    const notInProgressPreviousYears = `((status=UNDER_CONSTRUCTION OR status=INFO_GATHERING OR status=UNCLAIMED OR status=PENDING) AND (date_created < "${startOfTheYear}"))`

    switch (period) {
      case JobPeriodFilterOption.Current:
        this.period = `${claimed} OR ${submittedCurrentYear} OR ${createdCurrentYear}`
        return this
      case JobPeriodFilterOption.Old:
        this.period = `${closedPreviousYears} OR ${notInProgressPreviousYears}`
        return this
      default:
        return this
    }
  }

  /**
   * Searches for jobs based on their archived status.
   */
  withArchivalStatus(archived?: boolean) {
    if (!archived) {
      return this
    }

    this.archived = condition('archived', '=', 'true')
    return this
  }

  /**
   * Searches for pending jobs whose advance date has been reached or was never set.
   */
  withReadyToAdvance(readyToAdvance?: boolean) {
    if (!readyToAdvance) {
      return this
    }

    const status = condition('status', '=', 'PENDING')
    const dateReached = condition(
      'advance_allowed_time',
      '<=',
      new Date().toISOString(),
    )
    this.readyToAdvance = `${status} AND (${dateReached} OR advance_allowed_time = null)`
    return this
  }

  /**
   * Returns range query parts.
   */
  rangeQueryParts(
    fieldString: FieldString,
    range: DateRange | NumberRange,
  ): string[] {
    const queryParts = []

    if ('from' in range) {
      if (range.from) {
        queryParts.push(condition(fieldString, '>=', range.from.toISOString()))
      }
      if (range.to) {
        queryParts.push(condition(fieldString, '<=', range.to.toISOString()))
      }
    } else {
      if (range.min) {
        queryParts.push(condition(fieldString, '>=', range.min))
      }
      if (range.max) {
        queryParts.push(condition(fieldString, '<=', range.max))
      }
    }

    return queryParts
  }

  /**
   * Builds the query string.
   *
   * @returns
   */
  build(): string {
    return [
      this.legends,
      this.workspaces,
      this.statuses,
      this.progressionSteps,
      this.jobScopeChangeStatus,
      this.dateCreated,
      this.dateClosed,
      this.dateClaimed,
      this.dateSubmitted,
      this.providerCutAmount,
      this.teams,
      this.period,
      this.archived,
      this.readyToAdvance,
      this.members,
      this.serviceItems,
      this.documentTypes,
    ]
      .filter((i) => i)
      .map((i) => `(${i})`)
      .join(' AND ')
  }
}

/**
 * Generates a condition and escapes the passed value.
 *
 * @param field
 * @param operator
 * @param value
 * @returns
 */
function condition(
  field: string,
  operator: ConditionOperator,
  value: any,
): string {
  // eslint-disable-next-line no-useless-escape
  return `${field}${operator}\"${value}\"`
}

type ConditionOperator = '=' | '>' | '<' | '>=' | '<=' | ':'

enum FieldString {
  DateCreated = 'date_created',
  DateSubmitted = 'date_submitted',
  DateClaimed = 'date_claimed',
  DateCompleted = 'date_completed',
  ProviderCutAmount = 'provider_cut_amount',
}

/**
 * List of job statuses that are not 'CLOSED'.
 */
export const isNotJobStatusClosed: JobStatus[] = [
  'UNDER_CONSTRUCTION',
  'CLAIMED',
  'ON_HOLD',
  'IDLE',
  'INFO_GATHERING',
  'UNCLAIMED',
  'PENDING',
]
